/**
 * PathOfferCapChips — "CAP used/limit" chips for a path row's offers
 * (MD-1147). Joins the row's tracker offer ids to GET /offer-caps; offers
 * without a bank cap render nothing. Green under 80% of the limit, amber from
 * 80%, red once reached; an uncounted cap shows the limit with "—". Rendered on the
 * Flow Optimizer's path rows, the Banks → Paths table and the Flows tree (MD-1269).
 * Renders nothing outside a QueryClientProvider (isolated row specs).
 * For Media Devoted | Created By James Alexandrov
 */

import { useContext, type FC, type ReactNode } from 'react';
import { QueryClientContext } from '@tanstack/react-query';

import { capTimeframeNote, trackerFromPlatform } from '@trafficpulse/shared';

import { capUsageFor } from '@/features/offer-caps/lib/cap-usage-for';
import { cn, formatDateTimeGb } from '@/lib/utils';
import { useOfferCapUsage, type OfferCapUsage } from '@/features/bank-stats';
import { CAP_TONE_CHIP, capUsageTone } from '@/features/offer-caps';

// 'per day (resets 00:00 Danish time)', 'per week (…)', 'per calendar month (…)', 'lifetime'.
const timeframeLabel = (u: OfferCapUsage): string => capTimeframeNote(u.capTimeframe);

// Green under 80% of the limit, amber from 80%, red once reached — the same
// scale as the Offers table's CAP column.
const chipTone = (u: OfferCapUsage): string =>
  CAP_TONE_CHIP[capUsageTone(u.cap, u.used, u.reached)];

const OfferCapChip: FC<{ usage: OfferCapUsage; paused?: boolean }> = ({
  usage,
  paused = false,
}) => {
  const used = usage.used == null ? '—' : String(usage.used);
  const counted = usage.used != null ? `${used} of ${usage.cap}` : 'not counted yet';
  const at = usage.countedAt ? ` (counted ${formatDateTimeGb(usage.countedAt)})` : '';
  // Paused for its cap on THIS path (off the tracker path, link kept).
  const pausedNote = paused ? 'PAUSED on this path for its cap — ' : '';
  return (
    <span
      className={cn(
        'shrink-0 rounded border px-1 text-[10px] font-medium leading-4 tabular-nums',
        paused ? 'border-border bg-muted/60 text-muted-foreground line-through' : chipTone(usage),
      )}
      title={`${pausedNote}${usage.offerName} — CAP ${usage.capType} ${timeframeLabel(usage)}: ${counted}${at}`}
    >
      CAP {used}/{usage.cap}
    </span>
  );
};

export interface PathOfferCapChipsProps {
  /** The row's tracker; only legacy null/undefined folds into Voluum. */
  platform: string | null | undefined;
  /** The path's TRACKER offer ids. */
  offerIds: string[];
  /** Path ids (tracker or bank) resolved to their capped offers through
   *  GET /offer-caps — for rows that only know the path (the Queue tab). */
  pathIds?: string[];
  /** Rendered when nothing here has a cap (default: nothing). */
  empty?: ReactNode;
  className?: string;
}

const Chips: FC<PathOfferCapChipsProps> = ({
  platform: rawPlatform,
  offerIds,
  pathIds = [],
  empty = null,
  className,
}) => {
  const platform = trackerFromPlatform(rawPlatform);
  const { data: response } = useOfferCapUsage(offerIds.length > 0 || pathIds.length > 0);
  const data = capUsageFor(rawPlatform, response);
  const ids = new Set([...offerIds, ...pathIds.flatMap((p) => data?.pathOffers[p] ?? [])]);
  const capped = [...ids]
    .map((id) => [id, data?.offers[id]] as const)
    .filter(([, u]) => u != null && (u.platform ?? platform) === platform);
  if (capped.length === 0) return <>{empty}</>;
  const paused = new Set(pathIds.flatMap((p) => data?.pausedPathOffers?.[p] ?? []));
  return (
    <span className={cn('flex shrink-0 items-center gap-1', className)}>
      {capped.map(([id, u]) => (
        <OfferCapChip key={id} usage={u as OfferCapUsage} paused={paused.has(id)} />
      ))}
    </span>
  );
};

export const PathOfferCapChips: FC<PathOfferCapChipsProps> = (props) => {
  // The rows render in isolation in specs (no query client) — no chips there.
  const client = useContext(QueryClientContext);
  return client ? <Chips {...props} /> : <>{props.empty ?? null}</>;
};

export default PathOfferCapChips;
